/**
 * La fiche d'un candidat ouverte en panneau, par-dessus sa campagne.
 *
 * On passe d'un candidat à l'autre sans revenir à la table (‹ ›, dans l'ordre
 * de la table), et la fiche s'ouvre en pleine page pour être partagée. Le
 * candidat ouvert vit dans l'URL (`?candidat=…`) : le lien se colle tel quel,
 * et « précédent » du navigateur referme le panneau.
 */
import { PanneauLateral } from "@repo/ui";
import { ChevronLeft, ChevronRight, Maximize2, X } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";

type Props = {
  parcoursId: string;
  /** Candidats de la campagne, dans l'ordre de la table d'où l'on vient. */
  candidats: readonly { _id: string; prenomCandidat?: string; nomCandidat?: string }[];
  hrefCandidat: (parcoursId: string) => string;
  /** Fiche en pleine page (lien partageable). */
  hrefPleinePage: string;
  onFermer: () => void;
  children: ReactNode;
};

const nomDe = (c: { prenomCandidat?: string; nomCandidat?: string }) =>
  `${c.prenomCandidat ?? ""} ${c.nomCandidat ?? ""}`.trim() || "Candidat";

const classeBouton =
  "inline-flex h-8 items-center gap-1.5 rounded-full border border-charte-line px-3 text-sm transition-colors hover:bg-charte-hover aria-disabled:pointer-events-none aria-disabled:opacity-40";

export function PanneauCandidat({
  parcoursId,
  candidats,
  hrefCandidat,
  hrefPleinePage,
  onFermer,
  children,
}: Props) {
  const index = candidats.findIndex((c) => c._id === parcoursId);
  const courant = candidats[index];
  const precedent = index > 0 ? candidats[index - 1] : undefined;
  const suivant = index >= 0 ? candidats[index + 1] : undefined;

  const lienVoisin = (voisin: (typeof candidats)[number] | undefined, sens: "precedent" | "suivant") => {
    const icone =
      sens === "precedent" ? (
        <ChevronLeft className="h-4 w-4" />
      ) : (
        <ChevronRight className="h-4 w-4" />
      );
    const libelle = sens === "precedent" ? "Candidat précédent" : "Candidat suivant";
    return voisin ? (
      <Link
        to={hrefCandidat(voisin._id)}
        replace
        className={classeBouton}
        aria-label={`${libelle} : ${nomDe(voisin)}`}
        title={nomDe(voisin)}
      >
        {icone}
      </Link>
    ) : (
      <span className={classeBouton} aria-disabled="true" aria-label={libelle}>
        {icone}
      </span>
    );
  };

  return (
    <PanneauLateral
      ouvert
      onFermer={onFermer}
      libelle={courant ? `Fiche de ${nomDe(courant)}` : "Fiche candidat"}
      entete={
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {lienVoisin(precedent, "precedent")}
            {lienVoisin(suivant, "suivant")}
            {index >= 0 && (
              <span className="text-sm text-muted-foreground">
                Candidat {index + 1} sur {candidats.length}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Link to={hrefPleinePage} className={classeBouton}>
              <Maximize2 className="h-4 w-4" />
              Pleine page
            </Link>
            <button
              type="button"
              onClick={onFermer}
              aria-label="Fermer la fiche"
              className="rounded-full p-2 transition-colors hover:bg-charte-hover-strong"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      }
    >
      {children}
    </PanneauLateral>
  );
}
